import React, { useState, useEffect, useCallback } from "react";
import API from "../../utils/api";
import toast from "react-hot-toast";
import { useAuth } from "../../App";

// ── CSS ───────────────────────────────────────────────────────────────────────
const CSS = `
  .att-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 4px;
  }
  .att-kpi {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: 12px;
  }
  @media(max-width:680px){
    .att-grid { grid-template-columns: repeat(7, 1fr) !important; gap: 2px; }
  }
`;

const MONTHS_FULL  = ["January","February","March","April","May","June",
                      "July","August","September","October","November","December"];
const DAY_LABELS   = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

const STATUS_META = {
  present:       { color:"#34d399", bg:"rgba(52,211,153,0.15)",  icon:"✅", label:"Present"       },
  late:          { color:"#fbbf24", bg:"rgba(251,191,36,0.15)",  icon:"⏰", label:"Late"           },
  half_day:      { color:"#22d3ee", bg:"rgba(34,211,238,0.15)",  icon:"🌓", label:"Half Day"       },
  absent:        { color:"#f87171", bg:"rgba(248,113,113,0.15)", icon:"❌", label:"Absent"         },
  planned_leave: { color:"#a78bfa", bg:"rgba(167,139,250,0.15)", icon:"🏖️", label:"Leave"         },
  holiday:       { color:"#94a3b8", bg:"rgba(148,163,184,0.15)", icon:"🎉", label:"Holiday"        },
};

const currentYear  = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1; // 1-based
const YEARS = Array.from({ length: 3 }, (_, i) => currentYear - i);

function padded(n) { return String(n).padStart(2, "0"); }
function hhMM(ts) {
  if (!ts) return "—";
  return new Date(ts).toLocaleTimeString("en-IN", { hour:"2-digit", minute:"2-digit", hour12:true });
}
function fmtHrs(h) {
  if (!h) return "—";
  const v = parseFloat(h);
  const hrs = Math.floor(v);
  const min = Math.round((v - hrs) * 60);
  return min > 0 ? `${hrs}h ${min}m` : `${hrs}h`;
}

export default function MyAttendance() {
  const { user } = useAuth();
  const accent   = user?.company === "ebekmed" ? "#2AB5A5" : "#E31E24";

  const [year,    setYear]    = useState(currentYear);
  const [month,   setMonth]   = useState(currentMonth);
  const [records, setRecords] = useState([]);   // array of attendance rows for this month
  const [summary, setSummary] = useState([]);   // yearly monthly summary
  const [loading, setLoading] = useState(false);
  const [hovered, setHovered] = useState(null); // date string being hovered on calendar

  // ── Fetch monthly records ────────────────────────────────────────────────
  const fetchMonth = useCallback(async (yr, mo) => {
    setLoading(true);
    const monthStr = `${yr}-${padded(mo)}`;
    try {
      const res = await API.get(`/attendance/my?month=${monthStr}`);
      setRecords(res.data || []);
    } catch {
      toast.error("Failed to load attendance");
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Fetch yearly summary (for the stats strip) ───────────────────────────
  const fetchSummary = useCallback(async (yr) => {
    try {
      const res = await API.get(`/attendance/my/summary?year=${yr}`);
      setSummary(res.data || []);
    } catch {
      setSummary([]);
    }
  }, []);

  useEffect(() => { fetchMonth(year, month); },  [year, month, fetchMonth]);
  useEffect(() => { fetchSummary(year); },        [year, fetchSummary]);

  // ── Build a map of date → record for O(1) calendar lookup ───────────────
  const recMap = {};
  records.forEach(r => {
    recMap[String(r.work_date).substring(0, 10)] = r;
  });

  // ── Calendar math ────────────────────────────────────────────────────────
  const firstDay   = new Date(year, month - 1, 1).getDay();  // 0=Sun
  const daysInMonth = new Date(year, month, 0).getDate();

  const calendarCells = [];
  for (let i = 0; i < firstDay; i++) calendarCells.push(null);
  for (let d = 1; d <= daysInMonth; d++) calendarCells.push(d);

  // ── Monthly KPIs ─────────────────────────────────────────────────────────
  const kpi = {
    present:  records.filter(r => r.status === "present").length,
    late:     records.filter(r => r.status === "late").length,
    half_day: records.filter(r => r.status === "half_day").length,
    absent:   records.filter(r => r.status === "absent").length,
    leave:    records.filter(r => r.status === "planned_leave").length,
    holiday:  records.filter(r => r.status === "holiday").length,
    hours:    records.reduce((s, r) => s + parseFloat(r.work_hours || 0), 0),
  };

  // ── Yearly totals from summary ───────────────────────────────────────────
  const ytd = summary.reduce((acc, row) => {
    acc.present  += parseInt(row.present  || 0);
    acc.late     += parseInt(row.late     || 0);
    acc.absent   += parseInt(row.absent   || 0);
    acc.leave    += parseInt(row.on_leave || 0);
    acc.hours    += parseFloat(row.total_hours || 0);
    return acc;
  }, { present:0, late:0, absent:0, leave:0, hours:0 });

  // Hovered record
  const hovRec = hovered ? recMap[hovered] : null;

  const txtW = "#f9fafb";
  const txtM = "#d1d5db";
  const txtD = "#9ca3af";
  const selStyle = {
    padding:"7px 12px", borderRadius:8,
    border:"1.5px solid rgba(255,255,255,0.2)",
    background:"#1a1b2e", color:"#f9fafb",
    fontSize:13, fontFamily:"inherit", colorScheme:"dark",
  };
  const labelSt = { fontSize:11, fontWeight:700, color:txtD, textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:4 };

  return (
    <>
      <style>{CSS}</style>
      <div>

        {/* Header */}
        <div style={{ marginBottom:20 }}>
          <h1 className="page-title">🗓️ My Attendance</h1>
          <p className="page-subtitle">View your daily check-in/check-out records synced from the Sales CRM</p>
        </div>

        {/* ── Yearly summary strip ── */}
        <div className="card" style={{ marginBottom:16 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12, flexWrap:"wrap", gap:8 }}>
            <div style={{ fontWeight:700, fontSize:14, color:txtW }}>📅 {year} Overview</div>
            <select value={year} onChange={e => setYear(Number(e.target.value))} style={selStyle}>
              {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div className="att-kpi">
            {[
              { label:"Days Present",  value:ytd.present, icon:"✅", color:"#34d399", bg:"rgba(52,211,153,0.12)"  },
              { label:"Late Arrivals", value:ytd.late,    icon:"⏰", color:"#fbbf24", bg:"rgba(251,191,36,0.12)"  },
              { label:"Days Absent",   value:ytd.absent,  icon:"❌", color:"#f87171", bg:"rgba(248,113,113,0.12)" },
              { label:"Leaves Taken",  value:ytd.leave,   icon:"🏖️",color:"#a78bfa", bg:"rgba(167,139,250,0.12)" },
              { label:"Total Hours",   value:fmtHrs(ytd.hours), icon:"⏱️", color:"#22d3ee", bg:"rgba(34,211,238,0.12)" },
            ].map(k => (
              <div key={k.label} style={{ background:k.bg, borderRadius:10, padding:"12px 14px", border:`1px solid ${k.color}30` }}>
                <div style={{ fontSize:10, fontWeight:700, color:k.color, textTransform:"uppercase", letterSpacing:"0.04em" }}>{k.label}</div>
                <div style={{ fontSize:18, fontWeight:800, color:k.color, marginTop:4 }}>{k.icon} {k.value}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Month selector ── */}
        <div className="card" style={{ marginBottom:16 }}>
          <div style={{ display:"flex", alignItems:"flex-end", gap:12, flexWrap:"wrap" }}>
            <div>
              <div style={labelSt}>Month</div>
              <select value={month} onChange={e => setMonth(Number(e.target.value))} style={selStyle}>
                {MONTHS_FULL.map((m, i) => <option key={i} value={i+1}>{m}</option>)}
              </select>
            </div>
            <div>
              <div style={labelSt}>Year</div>
              <select value={year} onChange={e => { setYear(Number(e.target.value)); }} style={selStyle}>
                {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <button
              onClick={() => fetchMonth(year, month)}
              style={{ padding:"8px 20px", borderRadius:8, border:`2px solid ${accent}`, background:accent, color:"white", fontWeight:700, fontSize:13, cursor:"pointer" }}>
              🔄 Refresh
            </button>
            <div style={{ marginLeft:"auto", fontSize:12, color:txtD, alignSelf:"center" }}>
              {loading ? "Loading…" : `${records.length} records for ${MONTHS_FULL[month-1]} ${year}`}
            </div>
          </div>
        </div>

        {/* ── Monthly KPI strip ── */}
        <div className="att-kpi" style={{ marginBottom:16 }}>
          {[
            { label:"Present",  value:kpi.present,  icon:"✅", color:"#34d399", bg:"rgba(52,211,153,0.12)"  },
            { label:"Late",     value:kpi.late,     icon:"⏰", color:"#fbbf24", bg:"rgba(251,191,36,0.12)"  },
            { label:"Half Day", value:kpi.half_day, icon:"🌓", color:"#22d3ee", bg:"rgba(34,211,238,0.12)"  },
            { label:"Absent",   value:kpi.absent,   icon:"❌", color:"#f87171", bg:"rgba(248,113,113,0.12)" },
            { label:"Leave",    value:kpi.leave,    icon:"🏖️",color:"#a78bfa", bg:"rgba(167,139,250,0.12)" },
            { label:"Work Hrs", value:fmtHrs(kpi.hours), icon:"⏱️", color:"#818cf8", bg:"rgba(129,140,248,0.12)" },
          ].map(k => (
            <div key={k.label} style={{ background:k.bg, borderRadius:10, padding:"10px 12px", border:`1px solid ${k.color}30` }}>
              <div style={{ fontSize:10, fontWeight:700, color:k.color, textTransform:"uppercase", letterSpacing:"0.04em" }}>{k.label}</div>
              <div style={{ fontSize:16, fontWeight:800, color:k.color, marginTop:3 }}>{k.icon} {k.value}</div>
            </div>
          ))}
        </div>

        {/* ── Calendar view ── */}
        <div className="card" style={{ marginBottom:16 }}>
          <h3 style={{ fontWeight:700, fontSize:14, color:txtW, marginBottom:14 }}>
            📆 {MONTHS_FULL[month-1]} {year} — Calendar View
          </h3>

          {loading ? (
            <div style={{ textAlign:"center", padding:"40px 0", color:txtD }}>⏳ Loading…</div>
          ) : (
            <>
              {/* Day labels */}
              <div className="att-grid" style={{ marginBottom:4 }}>
                {DAY_LABELS.map(d => (
                  <div key={d} style={{ textAlign:"center", fontSize:10, fontWeight:700, color:txtD, padding:"4px 0" }}>{d}</div>
                ))}
              </div>

              {/* Calendar cells */}
              <div className="att-grid">
                {calendarCells.map((day, idx) => {
                  if (!day) return <div key={`e${idx}`}/>;
                  const dateStr = `${year}-${padded(month)}-${padded(day)}`;
                  const rec  = recMap[dateStr];
                  const meta = rec ? STATUS_META[rec.status] : null;
                  const isToday = dateStr === new Date().toISOString().substring(0,10);
                  const isHov   = hovered === dateStr;
                  const dow     = new Date(dateStr).getDay();
                  const isWeekend = dow === 0 || dow === 6;

                  return (
                    <div
                      key={dateStr}
                      onMouseEnter={() => setHovered(dateStr)}
                      onMouseLeave={() => setHovered(null)}
                      style={{
                        borderRadius:8, padding:"6px 4px", textAlign:"center",
                        border:`1.5px solid ${isToday ? accent : isHov ? "rgba(255,255,255,0.3)" : "rgba(255,255,255,0.06)"}`,
                        background: meta ? meta.bg : isWeekend ? "rgba(255,255,255,0.02)" : "transparent",
                        cursor: rec ? "pointer" : "default",
                        transition:"all 0.12s",
                        transform: isHov && rec ? "scale(1.05)" : "none",
                        position:"relative",
                      }}>
                      <div style={{ fontSize:11, fontWeight:isToday?800:500, color:isToday?accent:meta?meta.color:isWeekend?"#4b5563":txtD }}>
                        {day}
                      </div>
                      {meta && (
                        <div style={{ fontSize:13, lineHeight:1.1, marginTop:2 }}>{meta.icon}</div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Hover tooltip */}
              {hovRec && (
                <div style={{ marginTop:14, padding:"12px 16px", background:"rgba(255,255,255,0.05)", borderRadius:10, border:"1px solid rgba(255,255,255,0.12)", fontSize:13, color:txtM }}>
                  <div style={{ fontWeight:700, color:txtW, marginBottom:6 }}>
                    {new Date(hovRec.work_date).toLocaleDateString("en-IN", { weekday:"long", day:"numeric", month:"long", year:"numeric" })}
                  </div>
                  <div style={{ display:"flex", flexWrap:"wrap", gap:16 }}>
                    <span>{STATUS_META[hovRec.status]?.icon} <strong style={{ color:STATUS_META[hovRec.status]?.color }}>{STATUS_META[hovRec.status]?.label}</strong></span>
                    <span>🕘 In: <strong style={{ color:txtW }}>{hhMM(hovRec.check_in_time)}</strong></span>
                    <span>🕔 Out: <strong style={{ color:txtW }}>{hhMM(hovRec.check_out_time)}</strong></span>
                    <span>⏱️ Hours: <strong style={{ color:txtW }}>{fmtHrs(hovRec.work_hours)}</strong></span>
                    {hovRec.notes && <span>📝 {hovRec.notes}</span>}
                  </div>
                </div>
              )}

              {/* Legend */}
              <div style={{ marginTop:14, display:"flex", flexWrap:"wrap", gap:10 }}>
                {Object.values(STATUS_META).map(m => (
                  <div key={m.label} style={{ display:"flex", alignItems:"center", gap:5, fontSize:11, color:txtD }}>
                    <div style={{ width:10, height:10, borderRadius:2, background:m.color }}/>
                    {m.icon} {m.label}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* ── List view ── */}
        <div className="card">
          <h3 style={{ fontWeight:700, fontSize:14, color:txtW, marginBottom:14 }}>
            📋 Detailed List — {MONTHS_FULL[month-1]} {year}
          </h3>

          {loading ? (
            <div style={{ textAlign:"center", padding:"30px 0", color:txtD }}>⏳ Loading…</div>
          ) : records.length === 0 ? (
            <div style={{ textAlign:"center", padding:"30px 0", color:txtD }}>
              No attendance records found for this month.<br/>
              <span style={{ fontSize:12 }}>Records are synced automatically when you check in on the Sales CRM.</span>
            </div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
              {records.map(rec => {
                const meta = STATUS_META[rec.status] || STATUS_META.present;
                const dow  = new Date(rec.work_date).toLocaleDateString("en-IN", { weekday:"short" });
                return (
                  <div key={rec.id} style={{
                    display:"flex", alignItems:"center", gap:12,
                    padding:"10px 14px", borderRadius:10,
                    background:meta.bg, border:`1px solid ${meta.color}30`,
                    flexWrap:"wrap",
                  }}>
                    {/* Date */}
                    <div style={{ minWidth:96 }}>
                      <div style={{ fontWeight:700, fontSize:13, color:txtW }}>
                        {new Date(rec.work_date).toLocaleDateString("en-IN", { day:"numeric", month:"short" })}
                      </div>
                      <div style={{ fontSize:11, color:txtD }}>{dow}</div>
                    </div>

                    {/* Status badge */}
                    <div style={{
                      padding:"3px 10px", borderRadius:20, fontSize:12, fontWeight:700,
                      color:meta.color, background:`${meta.color}20`, border:`1px solid ${meta.color}40`,
                      whiteSpace:"nowrap",
                    }}>
                      {meta.icon} {meta.label}
                    </div>

                    {/* Times */}
                    <div style={{ display:"flex", gap:16, flex:1, flexWrap:"wrap" }}>
                      <div>
                        <div style={{ fontSize:10, color:txtD, fontWeight:700 }}>CHECK IN</div>
                        <div style={{ fontSize:13, fontWeight:700, color:rec.check_in_time?"#34d399":txtD }}>
                          {hhMM(rec.check_in_time)}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize:10, color:txtD, fontWeight:700 }}>CHECK OUT</div>
                        <div style={{ fontSize:13, fontWeight:700, color:rec.check_out_time?"#22d3ee":txtD }}>
                          {hhMM(rec.check_out_time)}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize:10, color:txtD, fontWeight:700 }}>WORK HRS</div>
                        <div style={{ fontSize:13, fontWeight:700, color:"#818cf8" }}>
                          {fmtHrs(rec.work_hours)}
                        </div>
                      </div>
                    </div>

                    {/* Notes */}
                    {rec.notes && (
                      <div style={{ fontSize:12, color:txtD, fontStyle:"italic", maxWidth:200 }}>
                        📝 {rec.notes}
                      </div>
                    )}

                    {/* Source badge */}
                    {rec.source && (
                      <div style={{ fontSize:10, color:"#4b5563", padding:"2px 7px", background:"rgba(255,255,255,0.04)", borderRadius:10, whiteSpace:"nowrap" }}>
                        via {rec.source}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

      </div>
    </>
  );
}
